import { Link } from "react-router";
import { useBusy } from "../app/useRun";
import { usePanel } from "../app/usePanel";
import { useRuleEditor } from "../features/rule-editor/useRuleEditor";
import { greeting, toAttentionView, toJourneyView, type Stage } from "../lib/home-views";
import { toActivityView, ruleTriggers } from "../lib/rule-views";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Icon, type IconName } from "../ui/Icon";
import { Mascot } from "../ui/Mascot";
import { Avatar, PostCover } from "../ui/PostCover";

const ACTIVITY_PREVIEW_SIZE = 6;
const RULES_PREVIEW_SIZE = 4;

const STAGE_ICONS: Record<Stage, IconName> = {
  comment: "message-circle",
  public: "reply",
  dm: "send",
  reply: "message-square-reply",
};

/** The account's pulse at a glance: is it listening, what it has achieved, what just happened. */
export function SummaryPage() {
  const { stats, rules, events, mediaById, connection, metrics } = usePanel();
  const busy = useBusy();
  const editor = useRuleEditor();
  const journey = toJourneyView(stats);
  const attention = toAttentionView(stats);
  const activity = events.slice(0, ACTIVITY_PREVIEW_SIZE).map((event) => toActivityView(event, mediaById));
  const totalsByRule = new Map(metrics.map((row) => [row.ruleId, row]));
  const featured = [...rules]
    .sort((a, b) => Number(b.enabled) - Number(a.enabled) || b.id - a.id)
    .slice(0, RULES_PREVIEW_SIZE);
  const name = connection.username ? `@${connection.username}` : "";

  return (
    <div className="cs-page">
      <section className="cs-hello">
        <Mascot mood={attention?.tone === "danger" ? "worried" : "happy"} size={86} float />
        <div className="cs-hello-text">
          <div className="cs-eyebrow">
            <span className="cs-hello-live" data-connected={connection.connected}>
              <span className="cs-live-dot" />
              {connection.connected ? "En escucha" : "Simulación"}
            </span>
          </div>
          <h1 className="cs-hello-title">
            {greeting()}
            {name ? <span>, {name}</span> : null}
          </h1>
          <p className="cs-hello-sub">
            {stats.activeRules > 0
              ? `${stats.activeRules} ${stats.activeRules === 1 ? "automatización está" : "automatizaciones están"} convirtiendo comentarios en conversaciones.`
              : "Crea tu primera automatización y deja que cada comentario abra una conversación."}
          </p>
        </div>
        <div className="cs-hello-actions">
          <Button disabled={busy} onClick={() => editor.create()} icon={<Icon name="plus" size={16} />}>
            Nueva automatización
          </Button>
        </div>
      </section>

      {attention ? (
        <Link to={attention.link} className="cs-attention" data-tone={attention.tone}>
          <span className="cs-attention-icon">
            <Icon name={attention.tone === "danger" ? "triangle-alert" : "hourglass"} size={18} />
          </span>
          <span className="cs-attention-text">
            <strong>{attention.title}</strong>
            <span>{attention.text}</span>
          </span>
          <span className="cs-attention-link">
            {attention.linkLabel} <Icon name="arrow-right" size={16} />
          </span>
        </Link>
      ) : null}

      <section className="cs-journey" aria-labelledby="journey-title">
        <div className="cs-block-head">
          <h2 id="journey-title" className="cs-block-heading">
            El viaje de un comentario
          </h2>
          <Link to="/actividad" className="cs-link-button cs-link-button-small">
            Ver registro <Icon name="arrow-right" size={14} />
          </Link>
        </div>
        <ol className="cs-journey-track">
          {journey.map((step) => (
            <li key={step.stage} className="cs-journey-step" data-stage={step.stage}>
              <span className="cs-journey-icon">
                <Icon name={STAGE_ICONS[step.stage]} size={18} />
              </span>
              <span className="cs-journey-label">{step.label}</span>
              <span className="cs-journey-value">{step.value.toLocaleString("es-MX")}</span>
              <span className="cs-journey-hint">{step.hint}</span>
              <span className="cs-journey-bar" aria-hidden="true">
                <span style={{ width: `${step.fill}%` }} />
              </span>
              {step.rateLabel ? <span className="cs-journey-rate">{step.rateLabel} del paso anterior</span> : null}
            </li>
          ))}
        </ol>
      </section>

      <div className="cs-home-grid">
        <section className="cs-card cs-home-rules" aria-labelledby="home-rules-title">
          <div className="cs-block-head">
            <h2 id="home-rules-title" className="cs-block-heading">
              Tus automatizaciones
            </h2>
            <Link to="/automatizaciones" className="cs-link-button cs-link-button-small">
              Ver todas <Icon name="arrow-right" size={14} />
            </Link>
          </div>
          {featured.length > 0 ? (
            <ul className="cs-home-rule-list">
              {featured.map((rule) => {
                const onlyInbox = !ruleTriggers(rule).includes("comment");
                const totals = totalsByRule.get(rule.id);
                return (
                  <li key={rule.id}>
                    <button type="button" className="cs-home-rule" onClick={() => editor.edit(rule.id)}>
                      {onlyInbox ? (
                        <span className="cs-home-rule-inbox">
                          <Icon name="inbox" size={18} />
                        </span>
                      ) : (
                        <PostCover media={mediaById.get(rule.mediaId)} className="cs-home-rule-cover" compact />
                      )}
                      <span className="cs-home-rule-body">
                        <span className="cs-home-rule-name">{rule.name || "Sin nombre"}</span>
                        <span className="cs-home-rule-keys">
                          {rule.keywords.slice(0, 3).map((word) => (
                            <span key={word} className="cs-keyword-bubble">
                              {word}
                            </span>
                          ))}
                        </span>
                      </span>
                      <span className="cs-home-rule-side">
                        <span className="cs-home-rule-count">
                          <b>{totals?.comments ?? 0}</b> coment.
                        </span>
                        <span
                          className="cs-state-dot"
                          data-on={rule.enabled}
                          title={rule.enabled ? "Activa" : "En pausa"}
                        />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="cs-home-empty">
              <Mascot mood="wink" size={64} />
              <p>Aún no hay automatizaciones. La primera tarda un minuto.</p>
              <Button size="sm" onClick={() => editor.create()}>
                Crear la primera
              </Button>
            </div>
          )}
        </section>

        <section className="cs-card cs-home-feed" aria-labelledby="home-feed-title">
          <div className="cs-block-head">
            <h2 id="home-feed-title" className="cs-block-heading">
              Lo último
            </h2>
            <span className="cs-feed-live">
              <span className="cs-live-dot" /> en vivo
            </span>
          </div>
          {activity.length > 0 ? (
            <ul className="cs-feed">
              {activity.map((row) => (
                <li key={row.id} className="cs-feed-item">
                  <Avatar name={row.user} size={34} />
                  <div className="cs-feed-body">
                    <div className="cs-feed-meta">
                      <strong>{row.user}</strong>
                      <span>{row.time}</span>
                    </div>
                    <p className="cs-feed-bubble" title={row.commentFull}>
                      {row.comment || "—"}
                    </p>
                  </div>
                  <Badge tone={row.tone} size="sm">
                    {row.status}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : (
            <div className="cs-home-empty">
              <Mascot mood="sleepy" size={64} />
              <p>Sin actividad todavía. Los próximos comentarios aparecerán aquí.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
